/**
 * Checkbox Component
 * 
 * A customizable checkbox with label and description support.
 * Built on MUI Checkbox with HAAT styling.
 */

import React from 'react';
import MuiCheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { motion } from 'framer-motion';

// =============================================================================
// TYPES
// =============================================================================

export interface CheckboxProps {
  /** Checkbox label */
  label: string;
  /** Optional description below label */
  description?: string;
  /** Controlled checked state */
  checked: boolean;
  /** Change handler */
  onChange: (checked: boolean) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Custom color */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning';
  /** Indeterminate state (for "select all" scenarios) */
  indeterminate?: boolean;
  /** Additional class name */
  className?: string;
  /** Name attribute for form */
  name?: string;
  /** ID attribute */
  id?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  error = false,
  errorMessage,
  size = 'medium',
  color: _color = 'primary',
  indeterminate = false,
  className = '',
  name,
  id,
}) => {
  const hasError = error || !!errorMessage;

  return (
    <div className={`${className}`}>
      <FormControlLabel
        control={
          <MuiCheckbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            indeterminate={indeterminate}
            size={size}
            name={name}
            id={id}
            sx={{
              color: hasError ? '#c00035' : '#9ca3af',
              '&.Mui-checked': {
                color: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
              },
              '&.MuiCheckbox-indeterminate': {
                color: 'rgb(var(--color-primary-500))',
              },
              '&:hover': {
                backgroundColor: hasError
                  ? 'rgba(220, 38, 38, 0.04)'
                  : 'rgba(var(--color-primary-500), 0.04)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          />
        }
        label={
          <div className="flex flex-col">
            <span className={`
              text-sm font-medium
              ${disabled ? 'text-gray-400' : hasError ? 'text-[#c00035]' : 'text-gray-700'}
            `}>
              {label}
            </span>
            {description && (
              <span className={`
                text-xs mt-0.5
                ${disabled ? 'text-gray-300' : 'text-gray-500'}
              `}>
                {description}
              </span>
            )}
          </div>
        }
        disabled={disabled}
        sx={{
          alignItems: description ? 'flex-start' : 'center',
          marginLeft: '-9px',
          '& .MuiFormControlLabel-label': {
            marginTop: description ? '2px' : 0,
          },
        }}
      />
      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <FormHelperText error sx={{ marginLeft: '32px', marginTop: '-4px' }}>
            {errorMessage}
          </FormHelperText>
        </motion.div>
      )}
    </div>
  );
};

export default Checkbox;

